import axios from '@/plugins/axios'
import _ from 'lodash'
import * as actionTypes from '@/store/action-types'
import * as mutationTypes from '@/store/mutation-types'
import { GlobalState, PeriodicityPlannedAbsenceEmployee } from '@/types'
import {
  AbsenceEventDropPayload, AbsencePayload,
  AbsencesAssistantStartEndPayload,
  NewAbsencePayload, PeriodicityAbsenceDetails,
  PeriodicityAbsencePayload,
  PlanningAssistantPeriodicityPayload, UpdateAbsencePayload, UpdateAbsenceSeriesPayload
} from '@/types/payload'
import {
  AbsenceReasonResponse,
  AbsenceReasonResponseItem, AbsenceSerie, CreateAbsenceSeriesResponse, GetAbsenceSeriesResponse, ResponseBase
} from '@/types/service-response'
import { ShiftEditorPlanningPayload } from '@/model/planning/ShiftEditorPlanningPayload'
import { EmployeeAvailableForPlanning } from '@/model/planning/EmployeeAvailableForPlanning'
import { Employee } from '@/model/companyStructure/Employee'
import { AbsenceReason } from '@/model/absences/AbsenceReason'
import { Absence } from '@/model/absences/Absence'
import { SelectedEmployee } from '@/model/absences/SelectedEmployee'

const absencesActions = {

  [actionTypes.ABSENCES_ASSISTANT_RESET_STATE] ({ commit }: { commit: Function }): void {
    commit(mutationTypes.ABSENCES_ASSISTANT_RESET_STATE_MUTATION)
  },

  [actionTypes.ABSENCES_ASSISTANT_INIT_FROM_PAYLOAD] ({ commit }: { commit: Function }, payload: ShiftEditorPlanningPayload): void {
    commit(mutationTypes.ABSENCES_ASSISTANT_SET_DATA_FROM_PAYLOAD_MUTATION, _.cloneDeep(payload))
  },

  [actionTypes.ABSENCES_ASSISTANT_SET_START_END_DATA] ({ commit }: { commit: Function }, payload: AbsencesAssistantStartEndPayload): void {
    commit(mutationTypes.ABSENCES_ASSISTANT_SET_START_DATE_MUTATION, payload.dateStart)
    commit(mutationTypes.ABSENCES_ASSISTANT_SET_END_DATE_MUTATION, payload.dateEnd)
    commit(mutationTypes.ABSENCES_ASSISTANT_SET_ABSENCE_REASON_ITEM_MUTATION, payload.absenceReasonItem)
  },

  [actionTypes.ABSENCES_ASSISTANT_SET_ABSENCE_REASON_ITEM] ({ commit }: { commit: Function }, payload: AbsenceReason): void {
    commit(mutationTypes.ABSENCES_ASSISTANT_SET_ABSENCE_REASON_ITEM_MUTATION, payload)
  },

  [actionTypes.ABSENCES_ASSISTANT_SET_EMPLOYEE_LIST_FOR_PLANNING] ({ commit }: { commit: Function }, payload: EmployeeAvailableForPlanning[]): void {
    commit(mutationTypes.ABSENCES_ASSISTANT_SET_EMPLOYEE_LIST_FOR_PLANNING_MUTATION, payload)
  },

  [actionTypes.ABSENCES_ASSISTANT_SET_PLANNED_EMPLOYEE_LIST] ({ commit }: { commit: Function }, payload: SelectedEmployee[]): void {
    commit(mutationTypes.ABSENCES_ASSISTANT_SET_PLANNED_EMPLOYEE_LIST_MUTATION, payload)
  },

  [actionTypes.ABSENCES_ASSISTANT_SET_PERIODICITY_PLANNED_EMPLOYEE_LIST] ({ commit }: { commit: Function }, payload: PeriodicityPlannedAbsenceEmployee[]): void {
    commit(mutationTypes.ABSENCES_ASSISTANT_SET_PERIODICITY_PLANNED_EMPLOYEE_LIST_MUTATION, payload)
  },

  [actionTypes.ABSENCES_ASSISTANT_SET_ABSENCE_APPROVE] ({ commit }: { commit: Function }, payload: boolean): void {
    commit(mutationTypes.ABSENCES_ASSISTANT_SET_ABSENCE_APPROVE_MUTATION, payload)
  },

  [actionTypes.ABSENCES_ASSISTANT_SET_ABSENCE_DISAPPROVE] ({ commit }: { commit: Function }, payload: boolean): void {
    commit(mutationTypes.ABSENCES_ASSISTANT_SET_ABSENCE_DISAPPROVE_MUTATION, payload)
  },

  [actionTypes.ABSENCES_ASSISTANT_SET_CURRENT_ABSENCE] ({ commit }: { commit: Function }, payload: Absence | undefined): void {
    commit(mutationTypes.ABSENCES_ASSISTANT_SET_CURRENT_ABSENCE_MUTATION, payload)
  },

  [actionTypes.ABSENCES_ASSISTANT_SET_FILTERED_EMPLOYEES_BY_DEPARTMENT] ({ commit }: { commit: Function }, payload: Employee[]): void {
    commit(mutationTypes.ABSENCES_ASSISTANT_SET_FILTERED_EMPLOYEES_BY_DEPARTMENT_MUTATION, payload)
  },

  [actionTypes.ABSENCES_ASSISTANT_SET_PERIODICITY] ({ commit }: { commit: Function }, payload: PlanningAssistantPeriodicityPayload): void {
    commit(mutationTypes.ABSENCES_ASSISTANT_SET_PERIODICITY_MUTATION, payload)
  },

  [actionTypes.ABSENCES_ASSISTANT_SET_IS_EDITING_SERIE] ({ commit }: { commit: Function }, payload: PlanningAssistantPeriodicityPayload): void {
    commit(mutationTypes.ABSENCES_ASSISTANT_SET_IS_EDITING_SERIE_MUTATION, payload)
  },

  [actionTypes.ABSENCES_ASSISTANT_SET_PERIODICITY_SERIE] ({ commit }: { commit: Function }, payload: AbsenceSerie | undefined): void {
    commit(mutationTypes.ABSENCES_ASSISTANT_SET_PERIODICITY_SERIE_MUTATION, payload)
  },

  async [actionTypes.LOAD_ABSENCE_REASON_LIST] ({ rootState }: { rootState: GlobalState }): Promise<Array<AbsenceReason>> {
    const response = await axios.post('/api/Abwesenheiten', null,{
      params: {
        'sprache': rootState.selectedUiLanguage!.syzId
      }
    })
    const data: AbsenceReasonResponse = response.data.filter((item: AbsenceReasonResponseItem) => item.Sprache ===  rootState.selectedUiLanguage!.syzId)

    return AbsenceReason.createListFromResponse(data)
  },


  async [actionTypes.LOAD_ABSENCES] ({ rootState }: { rootState: GlobalState }, payload: any): Promise<Array<Absence>> {
    const response = await axios.post('/api/Abwesenheiten', null,{
      params: {
        'action': 'Grab',
        'RequestApproved': '',
        'RequestDisapproved': '',
        'RequestStatusBemerkungen': '',
        'WhoAproved': 1,
        'MRAID': '',
        'Mitarbeiter': 2,
        'DatumVon': payload.startDate,
        'DatumBis': payload.endDate,
        'Bemerkung': '',
        'Grund': '',
        'who': 1,
        'showAll': 0,
        'Arztzeugnis': '',
        'Arztzeugnis_GueltigBis': '',
        'mitarbeiterSecurity': 1,
        'component': 'div_Login',
        'sprache': rootState.selectedUiLanguage!.syzId,
        'Abteilung': payload.departmentId
      }
    })

    const absencesList: Array<Absence> = Absence.createListFromResponse(response.data).filter((item: Absence) =>
      item.profitCenterId === payload.profitCenterId && item.departmentId === payload.departmentId)

    return absencesList
  },

  async [actionTypes.CREATE_NEW_ABSENCE] ({ rootState }: { rootState: GlobalState }, payload: AbsencePayload): Promise<boolean> {
    const absencesList: Array<any> = payload.data.map((item: NewAbsencePayload) => {
      return {
        'action': 'Insert',
        'RequestApproved': item.absenceApproved,
        'RequestDisapproved': item.absenceDisapproved,
        'RequestStatusBemerkungen': item.statusComment,
        'WhoAproved': item.whoApproved,
        'MRAID': item.MRAID,
        'Mitarbeiter': item.employee,
        'DatumVon': item.dateStart,
        'DatumBis': item.dateEnd,
        'Bemerkung': item.comment,
        'Grund': item.reason,
        'who': item.who,
        'showAll': 0,
        'Arztzeugnis': item.medicalCertificate,
        'Arztzeugnis_GueltigBis': item.medicalCertificateValid,
        'mitarbeiterSecurity': item.employeeSecurity,
        'component': item.component,
        'sprache': rootState.selectedUiLanguage!.syzId
      }
    })

    const response = await axios.post('/api/Abwesenheiten', absencesList,{
      params: {
        'BulkProcessing': 1,
        'abteilung': payload.abteilung
      }
    })
    return response.data
  },

  async [actionTypes.CREATE_NEW_PERIODICITY_ABSENCE] ({ rootState }: { rootState: GlobalState }, payload: PeriodicityAbsencePayload): Promise<CreateAbsenceSeriesResponse> {
    const details = payload.details.map((item: PeriodicityAbsenceDetails) => {
      return {
        "Mitarbeiter": item.employee,
        "Bemerkung": item.comment,
        "Arztzeugnis": item.medicalCertificate,
        "Arztzeugnis_GueltigBis": item.medicalCertificateValid
      }
    })

    const response = await axios.post('/api/Abwesenheiten/CreateSeries',
      {
        "RequestApproved": payload.absenceApproved,
        "DatumVon": payload.dateStart,
        "DatumBis": payload.dateEnd,
        "Grund": payload.reason,
        "Sprache": rootState.selectedUiLanguage!.syzId,
        "Montag": payload.monday,
        "Dienstag": payload.tuesday,
        "Mittwoch": payload.wednesday,
        "Donnerstag": payload.thursday,
        "Freitag": payload.friday,
        "Samstag": payload.saturday,
        "Sonntag": payload.sunday,
        "Whlg": payload.whlg,
        "Einmalig": payload.unique,
        "EndAfternTimes": parseInt(payload.endAfterTimes) > 0 ? parseInt(payload.endAfterTimes) : 'null',
        "EndAfterDate": payload.endAfterDate ?  payload.endAfterDate : 'null',
        "WiederholungsRegel": payload.repetitionRule,
        "WiederholungsRegelUI":payload.repetitionRuleUI,
        "Details": details
      },
      {
      params: {
        'languageId': rootState.selectedUiLanguage!.syzId
      }
    })
    return response.data
  },

  async [actionTypes.EDIT_PERIODICITY_ABSENCE] ({ rootState }: { rootState: GlobalState }, payload: UpdateAbsenceSeriesPayload): Promise<ResponseBase> {
    const response = await axios.post('/api/Abwesenheiten/UpdateSeries', payload)

    return response.data
  },

  async [actionTypes.FETCH_ABSENCE_SERIE_DATA] ({ rootState }: { rootState: GlobalState }, payload: number): Promise<GetAbsenceSeriesResponse> {
    const response = await axios.get('/api/Abwesenheiten/GetSeries',{
      params: {
        'seriesId': payload,
        'languageId': rootState.selectedUiLanguage!.syzId
      }
    })
    return response.data
  },

  async [actionTypes.DELETE_ABSENCE_SERIE] ({ rootState }: { rootState: GlobalState }, payload: number): Promise<ResponseBase> {
    const response = await axios.post('/api/Abwesenheiten/EraseSeries', {
      SeriesId: payload
    })

    return response.data
  },

  async [actionTypes.EDIT_ABSENCE] ({ rootState }: { rootState: GlobalState }, payload: UpdateAbsencePayload): Promise<boolean> {
    const response = await axios.post('/api/Abwesenheiten',
      [
        {
          'action': 'Update',
          'RequestApproved': payload.data.absenceApproved,
          'RequestDisapproved': payload.data.absenceDisapproved,
          'RequestStatusBemerkungen': payload.data.statusComment,
          'WhoAproved': payload.data.whoApproved,
          'MRAID': payload.data.MRAID,
          'Mitarbeiter': payload.data.employee,
          'DatumVon': payload.data.dateStart,
          'DatumBis': payload.data.dateEnd,
          'Bemerkung': payload.data.comment,
          'Grund': payload.data.reason,
          'who': payload.data.who,
          'showAll': 0,
          'Arztzeugnis': payload.data.medicalCertificate,
          'Arztzeugnis_GueltigBis': payload.data.medicalCertificateValid,
          'mitarbeiterSecurity': payload.data.employeeSecurity,
          'component': payload.data.component,
          'ID': payload.data.id,
          'sprache': rootState.selectedUiLanguage!.syzId
        }
      ],{
      params: {
        'BulkProcessing': 1,
        'abteilung': payload.abteilung
      }
    })

    return response.data
  },

  async [actionTypes.DELETE_ABSENCES_CHECKED_LIST] ({ rootState }: { rootState: GlobalState }, payload: Array<number>): Promise<boolean> {
    const response = await axios.post('/api/Abwesenheiten', payload,{
      params: {
        'DeleteAbwesenheiten': 1,
      }
    })
    return response.data
  },

  async [actionTypes.DELETE_ABSENCE] ({ rootState }: { rootState: GlobalState }, payload: Array<number>): Promise<boolean> {
    const response = await axios.post('/api/Abwesenheiten', payload,{
      params: {
        'DeleteAbwesenheiten': 1,
      }
    })
    return response.data
  },

  async [actionTypes.APPROVED_ABSENCES_CHECKED_LIST] ({ rootState }: { rootState: GlobalState }, payload: Array<number>): Promise<void> {
    const response = await axios.post('/api/Abwesenheiten', payload,{
      params: {
        'RequestApproved': 1
      }
    })
    return response.data
  },

  async [actionTypes.ABSENCE_DRAG_SAVE] ({ rootState }: { rootState: GlobalState }, payload: AbsenceEventDropPayload): Promise<void> {
    const response = await axios.post('/api/Abwesenheiten', payload,{
      params: {
        'Id': payload.absenceId,
        'Mitarbeiter': payload.employeeId,
        'Start': payload.startDate,
        'Ende': payload.endDate
      }
    })
    return response.data
  }
}

export default absencesActions
